/**
 * NewSearch.tsx — natural-language entry. The recruiter describes who they want; we call /plan to turn
 * it into an editable filter tree + soft criteria, then hand off to the results view via onPlanned.
 */

import { useState } from 'react';
import { Sparkles, Loader2 } from 'lucide-react';
import { planQuery, type PlanResponse } from '@/lib/talentPool.api';
import { getApiErrorMessage } from '@/lib/http';
import DotMask from '@/components/DotMask';

const EXAMPLES = [
    'Senior backend engineers in Singapore who worked at a fintech, fluent in Mandarin',
    'Product designers in Europe, 5+ years, ex-startup, shipped 0→1',
    'Data scientists with a Master’s or PhD, NLP experience, open to relocating to the US',
    'Engineering managers in India who were previously hands-on ICs',
];

export function NewSearch({ onPlanned }: { onPlanned: (query: string, plan: PlanResponse) => void }) {
    const [q, setQ] = useState('');
    const [loading, setLoading] = useState(false);
    const [err, setErr] = useState('');

    const run = async (query: string) => {
        if (!query.trim() || loading) return;
        setLoading(true);
        setErr('');
        try {
            const plan = await planQuery(query.trim());
            onPlanned(query.trim(), plan);
        } catch (e) {
            setErr(getApiErrorMessage(e, 'Planning failed'));
            setLoading(false);
        }
    };

    return (
        <div className="relative isolate flex h-full items-center justify-center overflow-hidden bg-zinc-50 p-6">
            {/* Dot-shimmer backdrop, masked to a soft oval over the composer —
                mirrors the home page hero. Sits below the content (z-0). */}
            <div
                aria-hidden
                className="pointer-events-none absolute inset-x-0 -inset-y-24 z-0 overflow-hidden [-webkit-mask-image:radial-gradient(ellipse_52%_42%_at_50%_50%,#000_42%,transparent_82%)] [mask-image:radial-gradient(ellipse_52%_42%_at_50%_50%,#000_42%,transparent_82%)]"
            >
                <DotMask
                    background="transparent"
                    color="#C8C3BA"
                    accent="#C77DAB"
                    accentMix={0.5}
                    gap={6}
                    dotShape="circle"
                    dotWidth={2}
                    dotHeight={2}
                    density={1.05}
                    speed={2}
                    vignette={0.15}
                    style={{ height: '100%' }}
                />
            </div>

            <div className="relative z-10 w-full max-w-[680px]">
                <h1 className="mb-1 text-[20px] font-semibold text-zinc-900">Search from your talent pool.</h1>
                <p className="mb-4 text-[13px] text-zinc-500">Who are you looking for?</p>

                <textarea
                    value={q}
                    onChange={(e) => setQ(e.target.value)}
                    onKeyDown={(e) => {
                        if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) run(q);
                    }}
                    rows={4}
                    placeholder="e.g. Senior backend engineers in Singapore who worked at a fintech, fluent in Mandarin"
                    className="w-full resize-none rounded-xl border border-zinc-200 bg-white p-4 text-[14px] text-zinc-900 shadow-[0_18px_50px_-12px_rgba(0,0,0,0.14),0_6px_16px_-6px_rgba(0,0,0,0.08)] outline-none placeholder:text-zinc-400 focus:border-[#0D1830]"
                />
                {err && <div className="mt-2 text-[12px] text-red-600">{err}</div>}
                <div className="mt-3 flex items-center gap-3">
                    <button
                        type="button"
                        onClick={() => run(q)}
                        disabled={!q.trim() || loading}
                        className="inline-flex items-center gap-2 rounded-lg bg-[#0D1830] px-4 py-2.5 text-[14px] font-medium text-white hover:bg-[#1C2B4D] disabled:opacity-50"
                    >
                        {loading ? <Loader2 size={15} className="animate-spin" /> : <Sparkles size={15} />}
                        {loading ? 'Building filters…' : 'Build filters'}
                    </button>
                    <span className="text-[12px] text-zinc-400">⌘/Ctrl + Enter</span>
                </div>

                <div className="mt-8">
                    <div className="mb-2 text-[11px] font-semibold uppercase tracking-wide text-zinc-400">Try an example</div>
                    <div className="flex flex-col gap-1.5">
                        {EXAMPLES.map((ex) => (
                            <button
                                key={ex}
                                type="button"
                                onClick={() => {
                                    setQ(ex);
                                    run(ex);
                                }}
                                className="rounded-lg border border-zinc-200 bg-white px-3 py-2 text-left text-[13px] text-zinc-600 hover:border-[#0D1830] hover:text-zinc-900"
                            >
                                {ex}
                            </button>
                        ))}
                    </div>
                </div>
            </div>
        </div>
    );
}
